import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {setAlertSystemLayerHandle} from './layers/launchers/AlertSystemLayerHandle'
import {AppDispatch, fetchData, getAllowedUrlParams} from './thunk'
import {
    setBackendUrl,
    setFeatureAppId,
    setPreviewDateParam,
    setServiceType,
} from '../features/general/generalSlice'
import {useFeatureServices} from '../use-feature-services'
import {RootState} from './rootReducer'
import {
    resetConfigurationModelsData,
    resetSimulationsList,
    setModelsByPromotions,
} from '../features/configurationsModelsData/configurationModelsDataSlice'

import {Model} from '../types/Model'
import {getValidSimulationsWithModel} from '../shared/utilities'
import {DealerForm} from './pages/DealerForm'
import {Home} from './pages/Home'
import {setExternalLinkPopUpLayerHandle} from './layers/launchers/ExternalLinkPupUpLayerHandle'
import {setDisclaimerLayerHandle} from './layers/launchers/LegalNotesLayerHandle'
import {setTrackingServiceManager} from '../shared/trackingServiceHelper'
import {setMyDealerCode} from '../features/dealers/dealerSlice'
import {resetFilterValues} from '../features/filterValues/filterValuesSlice'
import {ROUTE_PATHS} from '../shared/variables'

interface PropsApp {
    featureAppId: string
    backendUrl: string
    serviceType: string
    previewDate: string | undefined
}

export function App(props: PropsApp): React.JSX.Element {
    const dispatch = useDispatch() as AppDispatch
    const {'layer-manager': layerManager} = useFeatureServices() as any
    const {'s2:history': historyService} = useFeatureServices() as any
    const {tracking: trackingManager} = useFeatureServices() as any
    const {'mydealer-service': myDealerService} = useFeatureServices() as any
    // Reset the module-level store synchronously on mount. Deferring this to
    // useLayoutEffect would make the server render return no page content,
    // including the H1, because effects do not run during SSR.
    const [isStoreReady] = useState(() => {
        dispatch(resetFilterValues())
        dispatch(resetConfigurationModelsData())

        return true
    })
    const [currentPathname, setCurrentPathname] = useState(
        historyService.history.location.pathname
    )

    const models = useSelector(
        (state: RootState) => state.configurationModelsData.fetchModels
    )

    const activeSimulations = useSelector(
        (state: RootState) =>
            state.configurationModelsData.fetchActiveSimulations
    )

    if (typeof props.featureAppId !== 'undefined') {
        dispatch(setFeatureAppId(props.featureAppId))
    }

    if (typeof props.backendUrl !== 'undefined') {
        dispatch(setBackendUrl(props.backendUrl))
    }

    if (typeof props.serviceType !== 'undefined') {
        dispatch(setServiceType(props.serviceType))
    }

    useEffect(() => {
        const savedPathname = sessionStorage.getItem('previousPathname')

        if (!savedPathname) {
            sessionStorage.setItem('previousPathname', currentPathname)
        } else if (savedPathname !== currentPathname) {
            if (currentPathname === ROUTE_PATHS.dealerForm) {
                dispatch(resetFilterValues())
            }

            sessionStorage.setItem('previousPathname', currentPathname)
        }
    }, [currentPathname])

    useEffect(
        () =>
            historyService.history.listen((location: {pathname: string}) => {
                setCurrentPathname(location.pathname)
            }),
        [historyService.history]
    )

    useEffect(() => {
        dispatch(setPreviewDateParam(props.previewDate))
    }, [props.previewDate])

    useEffect(() => {
        setAlertSystemLayerHandle(layerManager)
        setDisclaimerLayerHandle(layerManager)
        setExternalLinkPopUpLayerHandle(layerManager)

        setTrackingServiceManager(trackingManager)
    }, [])

    useEffect(() => {
        dispatch(fetchData)
    }, [])

    useEffect(() => {
        // The mount run of this effect still closes over the pre-reset
        // store values, so wait for the reset before deriving anything.
        if (!isStoreReady) {
            return
        }

        promotionsByModel()
    }, [isStoreReady, models, activeSimulations])

    const promotionsByModel = () => {
        const data: any = []

        if (!models || !activeSimulations) {
            return
        }

        dispatch(resetSimulationsList())

        models.map((model: Model) => {
            if (activeSimulations[model.id]) {
                model = {
                    ...model,
                    data: {
                        model: activeSimulations[model.id].model,
                        img_name: activeSimulations[model.id].img_name,
                        info_cta_url: activeSimulations[model.id].info_cta_url,
                        sorting: activeSimulations[model.id].sorting,
                        color_stage: activeSimulations[model.id].color_stage,
                        info_cta_url_params_pass_through:
                            activeSimulations[model.id]
                                .info_cta_url_params_pass_through,
                        simulations: getValidSimulationsWithModel(
                            activeSimulations[model.id].simulations,
                            activeSimulations[model.id].model
                        ),
                    },
                }

                if (model.data?.simulations && model.data.simulations.length) {
                    data.push(model)
                }
            }
        })
        data.sort((a: any, b: any) => (a.sorting > b.sorting ? 1 : -1))
        dispatch(setModelsByPromotions(data))
    }

    useEffect(() => {
        if (myDealerService) {
            const myDealerCode = myDealerService.getSelectedDealer()

            if (myDealerCode) {
                dispatch(setMyDealerCode(myDealerCode.id))
            }

            myDealerService.subscribe((dealerId: any) => {
                const dealerCode = dealerId?.id ?? ''

                if (dealerCode) {
                    dispatch(setMyDealerCode(dealerCode))
                } else {
                    dispatch(setMyDealerCode(''))
                }
            })
        }
    }, [myDealerService])

    useEffect(() => {
        dispatch(getAllowedUrlParams())
    }, [])

    return currentPathname === ROUTE_PATHS.dealerForm ? (
        <DealerForm />
    ) : (
        <Home />
    )
}
